// eslint-disable-next-line no-unused-vars
import React, { useEffect, useMemo, useState } from 'react';
import { bool, func, object, string } from 'prop-types';
import { useTranslation } from 'react-i18next';

import SelectDestinationContent from 'Components/Wishlists/AddToModal/SelectDestinationContent';
import useAnalytics from 'Hooks/useAnalytics';
import useFetch from 'Hooks/useFetch';
import useWishlists from 'Hooks/useWishlists';
import useOffers from 'Hooks/useOffers';
import useToastNotifications from 'Hooks/useToastNotifications';
import useUser from 'Hooks/useUser';
import usePermissions from 'Hooks/usePermissions';
import { API_OFFERS, PERMISSION } from 'Constants';
import isOfferExpired, {
  clampMarkupPercent,
  getOfferProductErrors,
  isProductUnavailableForOffer,
  normalizeWishlistProductPrice,
  OFFER_PRODUCT_STATUS_TRANSLATIONS
} from 'Utils/offers';

const getProductId = product => product?.productId || product?.id;

const getItemPayload = item => ({
  productId: String(getProductId(item)),
  quantity: Number(item.quantity || item.minQuantity || 1),
  ...(item.supplierId !== null
    && item.supplierId !== undefined
    && { supplierId: item.supplierId }),
  markupPercent: item.markupPercent === null || item.markupPercent === undefined
    ? null
    : clampMarkupPercent(item.markupPercent),
  unitCustomPrice: item.unitCustomPrice ?? null
});

const SelectDestination = ({
  product,
  sourceWishlist,
  action,
  open,
  onDestinationTypeChange,
  onModeChange,
  onComplete,
  onCancel
}) => {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const fetcher = useFetch();
  const toast = useToastNotifications();
  const { can } = usePermissions();
  const {
    data: {
      isImpersonated,
      defaultCurrency,
      withVat
    } = {},
    isLoading: userLoading
  } = useUser();
  const {
    data,
    defaultWishlist,
    getList,
    updateProduct
  } = useWishlists();
  const {
    offers,
    activeOfferId,
    currentDestination,
    addProductToOffer,
    setActiveOffer,
    setCurrentDestination
  } = useOffers();
  const canAccessOffers = isImpersonated === false
    && can(PERMISSION.ACCESS_ACCOUNT_FEATURES);
  const wishlistOnly = !canAccessOffers;
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [savedOffers, setSavedOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const wishlistItems = useMemo(
    () => Object.values(data || {})
      .filter(list => list.id !== sourceWishlist?.id)
      .map(list => ({ id: list.id, label: list.name })),
    [data, sourceWishlist]
  );
  const offerItems = useMemo(
    () => (canAccessOffers ? offers : [])
      .filter(offer => !isOfferExpired(offer, currentTime))
      .map(offer => ({
        id: `private:${ offer.id }`,
        offerId: offer.id,
        label: offer.name,
        storage: 'private',
        status: 'private'
      }))
      .concat(savedOffers
        .filter(offer => !isOfferExpired(offer, currentTime))
        .map(offer => ({
          id: `saved:${ offer.id }`,
          offerId: offer.id,
          label: offer.name,
          storage: 'saved',
          status: 'saved'
        }))),
    [canAccessOffers, currentTime, offers, savedOffers]
  );
  const initialType = wishlistOnly
    ? 'wishlist'
    : (currentDestination?.type || 'offer');
  const [destinationType, setDestinationType] = useState(initialType);
  const [selectedWishlist, setSelectedWishlist] = useState(
    currentDestination?.type === 'wishlist'
      ? currentDestination.id
      : defaultWishlist?.id || wishlistItems.at(0)?.id || null
  );
  let initialSelectedOffer = offerItems.at(0)?.id || null;
  if (currentDestination?.type === 'offer') {
    initialSelectedOffer = `${ currentDestination.storage || 'private' }:${ currentDestination.id }`;
  } else if (activeOfferId) {
    initialSelectedOffer = `${ currentDestination?.storage || 'private' }:${ activeOfferId }`;
  }
  const [selectedOffer, setSelectedOffer] = useState(initialSelectedOffer);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDestinationType(
      wishlistOnly ? 'wishlist' : (currentDestination?.type || 'offer')
    );
  }, [currentDestination?.type, open, wishlistOnly]);

  useEffect(() => {
    onDestinationTypeChange(destinationType);
  }, [destinationType, onDestinationTypeChange]);

  useEffect(() => {
    const canLoadOffers = open
      && !userLoading
      && canAccessOffers;

    if (!canLoadOffers) {
      setOffersLoading(false);
      return undefined;
    }

    let isActive = true;
    setOffersLoading(true);
    fetcher(API_OFFERS)
      .then(response => {
        if (!isActive) return;
        setSavedOffers(response.items);
      })
      .catch(error => {
        if (isActive && error?.code !== 20) setSavedOffers([]);
      })
      .finally(() => {
        if (isActive) setOffersLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [canAccessOffers, fetcher, open, userLoading]);

  useEffect(() => {
    const expirationTimer = window.setInterval(() => {
      setCurrentTime(Date.now());
    }, 30000);

    return () => window.clearInterval(expirationTimer);
  }, []);

  useEffect(() => {
    if (
      wishlistItems.length
      && (!selectedWishlist || !wishlistItems.some(item => item.id === selectedWishlist))
    ) {
      setSelectedWishlist(
        wishlistItems.some(item => item.id === defaultWishlist?.id)
          ? defaultWishlist.id
          : wishlistItems.at(0).id
      );
    }
  }, [defaultWishlist, selectedWishlist, wishlistItems]);

  useEffect(() => {
    if (!offerItems.length) {
      if (selectedOffer) {
        setSelectedOffer(null);
      }
      return;
    }
    if (!selectedOffer || !offerItems.some(item => item.id === selectedOffer)) {
      setSelectedOffer(
        offerItems.some(item => (
          String(item.offerId) === String(activeOfferId)
          && item.storage === currentDestination?.storage
        ))
          ? `${ currentDestination.storage }:${ activeOfferId }`
          : offerItems.at(0).id
      );
    }
  }, [activeOfferId, currentDestination, offerItems, selectedOffer]);

  const addToWishlist = async listId => {
    let list = Object.values(data || {}).find(item => item.id === listId);
    if (list && !('products' in list)) {
      list = await getList(list.id, defaultCurrency, withVat);
    }
    if (!list) {
      throw new Error(t('wishlists.errors.doesNotExist'));
    }
    const exists = (list?.products || [])
      .some(item => (item.id || item.productId) === (product.id || product.productId));
    if (exists) {
      if (action === 'move' && sourceWishlist?.id) {
        await updateProduct(sourceWishlist.id, {
          id: product.id,
          quantity: 0
        });
      }
      analytics.track('add_to_destination_modal_wishlist_add', {
        wishlist_id: listId,
        product_id: product.productId || product.id,
        result: action === 'move' ? 'moved_to_existing' : 'already_exists'
      });
      toast.addNotification({
        type: action === 'move' ? 'success' : 'info',
        title: t(action === 'move'
          ? 'wishlists.successfullyAddedToWishlist'
          : 'wishlists.productAlreadyExists')
      });
      return;
    }
    await updateProduct(listId, {
      id: product.id,
      quantity: product.quantity || product.minQuantity || 1
    });
    if (action === 'move' && sourceWishlist?.id) {
      await updateProduct(sourceWishlist.id, {
        id: product.id,
        quantity: 0
      });
    }
    setCurrentDestination({ type: 'wishlist', id: listId });
    analytics.track('add_to_destination_modal_wishlist_add', {
      wishlist_id: listId,
      product_id: product.productId || product.id,
      action: action || 'add',
      result: 'added'
    });
    toast.addNotification({
      type: 'success',
      title: t('wishlists.successfullyAddedToWishlist')
    });
  };

  const handleSubmit = async event => {
    event.preventDefault();
    analytics.track('add_to_destination_modal_submitted', {
      destination_type: destinationType,
      destination_id: destinationType === 'offer' ? selectedOffer : selectedWishlist,
      product_id: product.productId || product.id,
      action: action || 'add'
    });
    setLoading(true);
    try {
      if (destinationType === 'wishlist') {
        await addToWishlist(selectedWishlist);
      } else {
        const offerProduct = sourceWishlist?.id
          ? normalizeWishlistProductPrice(product)
          : product;
        const selectedOption = offerItems.find(item => item.id === selectedOffer);
        if (!selectedOption) {
          throw new Error(t('offers.selectDestination'));
        }

        let result;
        let offer;
        if (selectedOption.storage === 'private') {
          result = await addProductToOffer(
            selectedOption.offerId,
            offerProduct,
            { allowOutOfStock: true }
          );
          offer = offers.find(item => String(item.id) === String(selectedOption.offerId));
        } else {
          const response = await fetcher(
            `${ API_OFFERS }/${ encodeURIComponent(selectedOption.offerId) }`
          );
          offer = response;
          if (!offer || isOfferExpired(offer)) {
            result = { offer, expired: true };
          } else if (isProductUnavailableForOffer(product)) {
            result = { offer, unavailable: true };
          } else {
            const productId = getProductId(product);
            const alreadyExists = (offer.items || []).some(
              item => String(getProductId(item)) === String(productId)
            );
            if (alreadyExists) {
              result = { offer, alreadyExists: true };
            } else {
              const offerItem = {
                ...offerProduct,
                quantity: offerProduct.quantity || offerProduct.minQuantity || 1,
                markupPercent: offerProduct.markupPercent
                  ?? offer.defaultMarkupPercent
                  ?? 0,
                unitCustomPrice: offerProduct.unitCustomPrice ?? null
              };
              await fetcher(
                `${ API_OFFERS }/${ encodeURIComponent(selectedOption.offerId) }/items`,
                {
                  method: 'POST',
                  body: JSON.stringify(getItemPayload(offerItem))
                }
              );
              offer = {
                ...offer,
                items: (offer.items || []).concat(offerItem)
              };
              result = { offer, alreadyExists: false };
            }
          }
        }
        if (result?.expired) {
          throw new Error(t('offers.expiredReadOnly'));
        }
        if (result?.unavailable) {
          throw new Error(t('offers.productUnavailable'));
        }
        if (!result || !offer) {
          throw new Error(t('offers.selectDestination'));
        }
        if (action === 'move' && sourceWishlist?.id) {
          await updateProduct(sourceWishlist.id, {
            id: product.id,
            quantity: 0
          });
        }
        setActiveOffer(selectedOption.offerId, selectedOption.storage);
        analytics.track('add_to_destination_modal_offer_add', {
          offer_id: selectedOption.offerId,
          storage: selectedOption.storage,
          product_id: product.productId || product.id,
          result: result?.alreadyExists ? 'already_exists' : 'added'
        });
        toast.addNotification({
          type: result?.alreadyExists ? 'info' : 'success',
          title: result?.alreadyExists
            ? t('offers.productAlreadyExists')
            : t('offers.productAdded', { name: offer?.name || '' })
        });
      }
      onComplete();
    } catch (e) {
      const productErrors = getOfferProductErrors(e);
      const errorMessage = productErrors.length
        ? [...new Set(productErrors.map(productError => t(
          OFFER_PRODUCT_STATUS_TRANSLATIONS[productError.status]
          || 'offers.cartStatusProductUnavailable'
        )))].join(' ')
        : e.message || t('offers.selectDestination');
      analytics.track('add_to_destination_modal_failed', {
        destination_type: destinationType,
        destination_id: destinationType === 'offer' ? selectedOffer : selectedWishlist,
        product_id: product.productId || product.id
      });
      toast.addNotification({
        type: 'error',
        title: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <SelectDestinationContent
      product={ product }
      destinationType={ destinationType }
      wishlists={ wishlistItems }
      offers={ offerItems }
      selectedWishlist={ selectedWishlist }
      selectedOffer={ selectedOffer }
      loading={ loading }
      offersLoading={ offersLoading }
      wishlistOnly={ wishlistOnly }
      onDestinationTypeChange={ type => {
        analytics.track('add_to_destination_modal_destination_type_changed', {
          destination_type: type
        });
        setDestinationType(type);
      } }
      onWishlistChange={ ({ selectedItem }) => {
        analytics.track('add_to_destination_modal_wishlist_selected', {
          wishlist_id: selectedItem?.id || null
        });
        setSelectedWishlist(selectedItem?.id || null);
      } }
      onOfferChange={ ({ selectedItem }) => {
        analytics.track('add_to_destination_modal_offer_selected', {
          offer_id: selectedItem?.id || null
        });
        setSelectedOffer(selectedItem?.id || null);
      } }
      onCreateWishlist={ () => onModeChange('createWishlist') }
      onCreateOffer={ () => onModeChange('createOffer') }
      onSubmit={ handleSubmit }
      onCancel={ onCancel }
    />
  );
};

SelectDestination.propTypes = {
  product: object,
  sourceWishlist: object,
  action: string,
  open: bool,
  onDestinationTypeChange: func,
  onModeChange: func,
  onComplete: func,
  onCancel: func
};

export default SelectDestination;
